import { useEffect, useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  Folder,
  ChevronUp,
  AlertCircle
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import AssetField from '@/components/admin/AssetField';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  parent_id: string | null;
  position: number;
  product_count: number;
  children?: Category[];
}

interface CategoryFormData {
  name: string;
  slug: string;
  description: string;
  image_url: string;
  parent_id: string;
}

const INITIAL_FORM_DATA: CategoryFormData = {
  name: '',
  slug: '',
  description: '',
  image_url: '',
  parent_id: ''
};

const Categories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(INITIAL_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Category | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ categories: Category[] }>('/admin/categories');
      setCategories(buildTree(response.categories));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const buildTree = (flatCategories: Category[]): Category[] => {
    const map = new Map<string, Category>();
    const roots: Category[] = [];

    // Create map and initialize children arrays
    flatCategories.forEach((cat) => {
      map.set(cat.id, { ...cat, children: [] });
    });

    // Build tree structure
    flatCategories.forEach((cat) => {
      const node = map.get(cat.id)!;
      if (cat.parent_id && map.has(cat.parent_id)) {
        map.get(cat.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort by position
    const sortByPosition = (items: Category[]) => {
      items.sort((a, b) => a.position - b.position);
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          sortByPosition(item.children);
        }
      });
    };

    sortByPosition(roots);
    return roots;
  };

  const getFlatCategories = (): Category[] => {
    const flat: Category[] = [];
    const traverse = (items: Category[]) => {
      items.forEach((item) => {
        flat.push(item);
        if (item.children && item.children.length > 0) {
          traverse(item.children);
        }
      });
    };
    traverse(categories);
    return flat;
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData(INITIAL_FORM_DATA);
    setIsModalOpen(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image_url: category.image_url || '',
      parent_id: category.parent_id || ''
    });
    setIsModalOpen(true);
  };

  const handleInputChange = (field: keyof CategoryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (value: string) => {
    handleInputChange('name', value);
    if (!editingCategory || !formData.slug) {
      handleInputChange('slug', generateSlug(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        name: formData.name.trim(),
        slug: formData.slug.trim() || generateSlug(formData.name),
        description: formData.description.trim() || null,
        image_url: formData.image_url.trim() || null,
        parent_id: formData.parent_id || null
      };

      if (editingCategory) {
        await api.put(`/admin/categories/${editingCategory.id}`, payload);
      } else {
        await api.post('/admin/categories', payload);
      }

      setIsModalOpen(false);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (category.children && category.children.length > 0) {
      alert('Cannot delete category with subcategories. Please delete or move subcategories first.');
      return;
    }

    setDeleteConfirm(category);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await api.delete(`/admin/categories/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const moveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    try {
      const flatCategories = getFlatCategories();
      const currentIndex = flatCategories.findIndex((c) => c.id === categoryId);
      if (currentIndex === -1) return;

      const current = flatCategories[currentIndex];
      const siblings = flatCategories.filter((c) => c.parent_id === current.parent_id);
      const siblingIndex = siblings.findIndex((c) => c.id === categoryId);

      if (
        (direction === 'up' && siblingIndex === 0) ||
        (direction === 'down' && siblingIndex === siblings.length - 1)
      ) {
        return;
      }

      const swapIndex = direction === 'up' ? siblingIndex - 1 : siblingIndex + 1;
      const newPositions = siblings.map((cat, idx) => {
        if (idx === siblingIndex) {
          return { id: cat.id, position: siblings[swapIndex].position };
        }
        if (idx === swapIndex) {
          return { id: cat.id, position: siblings[siblingIndex].position };
        }
        return { id: cat.id, position: cat.position };
      });

      await api.put('/admin/categories/reorder', { positions: newPositions });
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder categories');
    }
  };

  const getParentOptions = (excludeId?: string): { label: string; value: string }[] => {
    const options: { label: string; value: string }[] = [{ label: 'None (Top Level)', value: '' }];

    const traverse = (items: Category[], depth = 0) => {
      items.forEach((cat) => {
        if (cat.id !== excludeId) {
          options.push({
            label: `${'  '.repeat(depth)}${cat.name}`,
            value: cat.id
          });
          if (cat.children && cat.children.length > 0) {
            traverse(cat.children, depth + 1);
          }
        }
      });
    };

    traverse(categories);
    return options;
  };

  if (error && !isModalOpen) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error loading categories</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600 mt-1">Organize your products into categories</p>
        </div>
        <Button variant="primary" size="md" onClick={openCreateModal}>
          <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
          Add Category
        </Button>
      </div>

      {/* Categories Tree */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-12 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : categories.length === 0 ? (
          <div className="p-12 text-center">
            <Folder className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No categories yet</h3>
            <p className="text-gray-600 mb-6">Create your first category to organize products</p>
            <Button variant="primary" size="md" onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
              Add Category
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {categories.map((category) => (
              <CategoryTreeItem
                key={category.id}
                category={category}
                level={0}
                expandedIds={expandedIds}
                onToggleExpand={toggleExpand}
                onEdit={openEditModal}
                onDelete={handleDelete}
                onMove={moveCategory}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCategory ? 'Edit Category' : 'Create Category'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Category Name"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., New Category"
            required
            autoFocus
          />

          <Input
            label="URL Slug"
            value={formData.slug}
            onChange={(e) => handleInputChange('slug', e.target.value)}
            placeholder="vegetable-seeds"
            helperText="Used in category URLs"
          />

          <Select
            label="Parent Category"
            value={formData.parent_id}
            onChange={(e) => handleInputChange('parent_id', e.target.value)}
            options={getParentOptions(editingCategory?.id)}
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Brief description of this category..."
            rows={3}
          />

          <AssetField
            specKey="category.image_url"
            label="Category image"
            value={formData.image_url}
            onChange={(url) => handleInputChange('image_url', url)}
            helperText="Shown on the shop's category cards. Optional."
          />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              size="md"
              type="button"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" size="md" type="submit" isLoading={isSaving}>
              {editingCategory ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Category"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm text-yellow-800">
                Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
              </p>
              {deleteConfirm && deleteConfirm.product_count > 0 && (
                <p className="text-sm text-yellow-800 mt-2">
                  This category has {deleteConfirm.product_count}{' '}
                  {deleteConfirm.product_count === 1 ? 'product' : 'products'}. They will not be
                  deleted but will become uncategorized.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" size="md" onClick={confirmDelete}>
              Delete Category
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

interface CategoryTreeItemProps {
  category: Category;
  level: number;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
}

const CategoryTreeItem = ({
  category,
  level,
  expandedIds,
  onToggleExpand,
  onEdit,
  onDelete,
  onMove
}: CategoryTreeItemProps) => {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedIds.has(category.id);

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors',
          level > 0 && 'bg-gray-25'
        )}
        style={{ paddingLeft: `${level * 2 + 1}rem` }}
      >
        {/* Expand/Collapse */}
        <button
          onClick={() => hasChildren && onToggleExpand(category.id)}
          className={cn(
            'p-1 rounded hover:bg-gray-200 transition-colors',
            !hasChildren && 'invisible'
          )}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600" aria-hidden="true" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>

        {/* Icon */}
        <div className="text-gray-500">
          {hasChildren ? (
            isExpanded ? (
              <FolderOpen className="w-5 h-5" aria-hidden="true" />
            ) : (
              <Folder className="w-5 h-5" aria-hidden="true" />
            )
          ) : (
            <Folder className="w-5 h-5" aria-hidden="true" />
          )}
        </div>

        {/* Category Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900">{category.name}</h3>
            <span className="text-sm text-gray-500">
              ({category.product_count} {category.product_count === 1 ? 'product' : 'products'})
            </span>
          </div>
          {category.description && (
            <p className="text-sm text-gray-600 truncate">{category.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMove(category.id, 'up')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
            aria-label="Move up"
          >
            <ChevronUp className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => onMove(category.id, 'down')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
            aria-label="Move down"
          >
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => onEdit(category)}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            aria-label="Edit category"
          >
            <Edit2 className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => onDelete(category)}
            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
            aria-label="Delete category"
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <>
          {category.children!.map((child) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              level={level + 1}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
            />
          ))}
        </>
      )}
    </>
  );
};

export default Categories;
